import { MeetingOrderedSession } from "@kalyzee/kast-web-sdk";
import React, { useContext, useEffect, useRef, useState } from "react";
import { appMessage } from "../../../helpers/message";
import { getExpertHomeParams } from "../../../helpers/storage";
import { debounce } from "lodash";
import { ActivityData, ExpertView, PopulatedMeetingData } from "../../../interfaces/populatedData";
import { useActivityData } from "../../../hooks/populatedData";

export interface ExpertMeetingOptions {}

export interface ExpertContextState {
  highlitghedRequestsPanelOpen: boolean;
  setHighlitghedRequestsPanelOpen: (open: boolean) => void;
  switchHighlitghedRequestsPanelOpen: () => void;

  populatedMeetingData: PopulatedMeetingData | undefined;
  setPopulatedMeetingData: (data?: PopulatedMeetingData) => void;
  availableViews: ExpertView[] | undefined;
  setAvailableViews: (data?: ExpertView[]) => void;
  currentView: ExpertView | undefined;
  setCurrentView: (view?: ExpertView) => void;

  syncMicrophone: boolean;
  setSyncMicrophone: (value: boolean) => void;
  audioStreamEnabled: boolean;
  setAudioStreamEnabled: (enabled: boolean) => void;
  camerasVideoStreamEnabled: boolean;
  setCamerasVideoStreamEnabled: (enabled: boolean) => void;
  screensVideoStreamEnabled: boolean;
  setScreensVideoStreamEnabled: (enabled: boolean) => void;
  volume: number; // 0 to 100 (0% to 100%)
  setVolume: (volume: number) => void;
  setVolumeSliderFocused: (value: boolean) => void;

  orderedSessions: MeetingOrderedSession[],
  setOrderedSessions: (value: MeetingOrderedSession[]) => void;
  
  spiedGroupId: string | undefined;
  setSpiedGroupId: (groupId: string | undefined) => void;

  webcam?: { mediaStream: MediaStream };
  setWebcam: (webcam: { mediaStream: MediaStream }) => void;

  activityData?: ActivityData;

  getMeetingOptions: (meetingId: string) => ExpertMeetingOptions | undefined;
  updateMeetingOptions: (meetingId: string, options: ExpertMeetingOptions) => void;
}

const ExpertContext = React.createContext<ExpertContextState | undefined>(undefined);

export interface ExpertContextProviderProps {
  children: React.ReactNode;
}

export const useExpertContext = (): ExpertContextState | undefined => useContext(ExpertContext);

export const useExpertContextState = () : ExpertContextState => {
  const [highlitghedRequestsPanelOpen, setHighlitghedRequestsPanelOpen] = useState(false);
  const [audioStreamEnabled, setAudioStreamEnabled] = useState(false);
  const [syncMicrophone, setSyncMicrophone] = useState(false);
  const [camerasVideoStreamEnabled, setCamerasVideoStreamEnabled] = useState(true);
  const [screensVideoStreamEnabled, setScreensVideoStreamEnabled] = useState(true);

  const [populatedMeetingData, setPopulatedMeetingData] = useState<PopulatedMeetingData | undefined>(undefined);
  const [availableViews, setAvailableViews] = useState<ExpertView[] | undefined>(undefined);
  const [currentView, setCurrentView] = useState<ExpertView | undefined>(undefined);

  const [volume, setVolume] = useState(-1);
  const volumeSliderFocusedRef = useRef(false);
  const [orderedSessions, setOrderedSessions] = useState<MeetingOrderedSession[]>([]);
  const postVolumeRef = useRef({
    setVolume: debounce((v) => {
      appMessage.postVolume(v);
    }, 100, { leading: true, maxWait: 100 }),
  })
  const [spiedGroupId, setSpiedGroupId] = useState<string | undefined>(undefined);

  const [webcam, setWebcam] = useState<{ mediaStream: MediaStream} | undefined>(undefined);

  const [meetingOptions, setMeetingOptions] = useState<{ [meetingId: string]: ExpertMeetingOptions }>({});

  const activityData = useActivityData(populatedMeetingData);

  const getMeetingOptions = (meetingId: string) => meetingOptions[meetingId];
  
  const updateMeetingOptions = (meetingId: string, update: ExpertMeetingOptions) => {
    setMeetingOptions((prev) => {
      const prevOptions = prev[meetingId] ?? {};
      const updatedOptions = { ...prevOptions, ...update };
      return { ...prev, [meetingId]: updatedOptions };
    });
  };

  useEffect(() => {
    const volumeListener = (volume: number) => {
      if (volumeSliderFocusedRef.current) return; // no update if slider is focused
      setVolume(volume);
    };
    appMessage.addEventListener("volume", volumeListener);
    return () => {
      appMessage.removeEventListener("volume", volumeListener);
    };
  }, []);

  useEffect(() => {
    if (syncMicrophone) appMessage.setMicrophoneData(audioStreamEnabled ? false : true);
    else {
      const expertHomeParams = getExpertHomeParams();
      const micOffOutside = expertHomeParams?.micOffOutsideOfAnEvent ?? false;
      appMessage.setMicrophoneData(micOffOutside);
    }
  }, [syncMicrophone, audioStreamEnabled]);

  const state: ExpertContextState = {
    highlitghedRequestsPanelOpen,
    setHighlitghedRequestsPanelOpen,
    switchHighlitghedRequestsPanelOpen: () => setHighlitghedRequestsPanelOpen((prev) => !prev),

    populatedMeetingData,
    setPopulatedMeetingData,
    availableViews,
    setAvailableViews,
    currentView,
    setCurrentView,

    syncMicrophone,
    setSyncMicrophone,
    audioStreamEnabled,
    setAudioStreamEnabled,
    camerasVideoStreamEnabled,
    setCamerasVideoStreamEnabled,
    screensVideoStreamEnabled,
    setScreensVideoStreamEnabled,

    volume,
    setVolume: (v) => {
      postVolumeRef.current.setVolume(v);
      setVolume(v);
    },
    setVolumeSliderFocused: (value) => volumeSliderFocusedRef.current = value,

    orderedSessions,
    setOrderedSessions,

    spiedGroupId,
    setSpiedGroupId,

    webcam,
    setWebcam,

    activityData,

    getMeetingOptions,
    updateMeetingOptions,
  }
  return state;
}

export const ExpertContextProvider = ({ children }: ExpertContextProviderProps) => {
  const state = useExpertContextState();
  return (
    <ExpertContext.Provider value={state}>
      {children}
    </ExpertContext.Provider>
  );
};